import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { StickyNote, Loader2, X } from 'lucide-react';
import { Order } from '../types';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { formatCurrency } from '../lib/utils';

interface AddOrderNoteModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
  onSaveNote?: (orderId: string, note: string) => Promise<void>;
}

const MAX_NOTE_LENGTH = 500;

export function AddOrderNoteModal({ order, isOpen, onClose, onSaveNote }: AddOrderNoteModalProps) {
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && order) {
      setNote(order.notes || '');
    } else {
      setNote('');
    }
  }, [isOpen, order]);

  const normalizedExistingNote = (order?.notes || '').trim();
  const normalizedCurrentNote = note.trim();
  const hasChanges = normalizedExistingNote !== normalizedCurrentNote;

  const handleSave = async () => {
    if (!order || !onSaveNote || isSaving) {
      return;
    }

    try {
      setIsSaving(true);
      await onSaveNote(order.id, normalizedCurrentNote);
      toast.success('Order note updated successfully');
      onClose();
    } catch (error) {
      console.error('Failed to save order note:', error);
      toast.error('Failed to update order note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !order) {
    return null;
  }

  const orderNumber = order.orderNumber || order.id;

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
          style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', duration: 0.5, bounce: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <StickyNote className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-xl font-bold text-white"
                    >
                      Add Customer Note
                    </motion.h2>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="flex flex-wrap gap-4 text-white/80 text-sm mt-1"
                    >
                      <span>Order: {orderNumber}</span>
                      <span>Customer: {order.customer.name}</span>
                      <span>Total: {order.total ? formatCurrency(order.total) : '—'}</span>
                    </motion.div>
                  </div>
                </div>
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white p-6 space-y-6">
              <div className="bg-white border border-amber-100 rounded-xl p-5 shadow-sm">
                <p className="text-sm text-gray-600">
                  This note appears inside future status update emails for this order so customers know what to expect.
                  Keep it concise and friendly.
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="order-note" className="text-sm font-medium text-gray-900">
                  Order Note
                </label>
                <Textarea
                  id="order-note"
                  value={note}
                  maxLength={MAX_NOTE_LENGTH}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="Add helpful context for this customer..."
                  className="min-h-[150px] resize-none"
                  disabled={isSaving}
                />
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>This is shared on customer-facing emails when the order status changes.</span>
                  <span>
                    {note.length}/{MAX_NOTE_LENGTH} characters
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-end gap-4">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button variant="outline" onClick={onClose} disabled={isSaving} className="px-6 h-11 rounded-xl font-medium">
                    Cancel
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    onClick={handleSave}
                    disabled={!hasChanges || isSaving}
                    className="bg-amber-500 hover:bg-amber-600 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-amber-500/25"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Note'
                    )}
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
